import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, TrendingUp } from "lucide-react";
import { cover, fmt, inr } from "./format";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const GRANULARITIES = [
  ["day", "Daily"],
  ["week", "Weekly"],
  ["month", "Monthly"],
  ["custom", "Custom"],
];

const BARS = [
  ["cashfree_rc", "bg-indigo-500", "Cashfree RC"],
  ["carinfo_rc", "bg-fuchsia-400", "CarInfo RC rescue"],
  ["carinfo_challan", "bg-emerald-500", "CarInfo challans"],
  ["bilapi_challan", "bg-sky-500", "BILAPI challans"],
];

function Stat({ label, value, sub, testid }) {
  return (
    <div className="rounded-lg border border-slate-200/80 bg-slate-50/60 px-3 py-2" data-testid={testid}>
      <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">{label}</div>
      <div className="text-lg font-bold text-slate-900 leading-tight">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">{sub}</div>}
    </div>
  );
}

/**
 * What vehicle data actually costs, counted from the call log, so the recharge
 * size picks itself. Includes what the Surepass fallback costs when the
 * primaries miss, which is the whole reason we moved off it.
 */
export default function SpendTrend() {
  const [granularity, setGranularity] = useState("week");
  const [range, setRange] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params =
        granularity === "custom"
          ? { granularity: "day", start: range.start, end: range.end }
          : { granularity };
      const res = await axios.get(`${API_URL}/dashboard/services/spend-trend`, { params });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the spend trend");
    } finally {
      setBusy(false);
    }
  }, [granularity, range.start, range.end]);

  useEffect(() => {
    if (granularity === "custom" && !(range.start && range.end)) return;
    load();
  }, [granularity, range.start, range.end, load]);

  const buckets = data?.buckets || [];
  const peak = buckets.reduce((mx, b) => Math.max(mx, b.total_inr || 0), 0) || 1;
  const run = data?.weekly_run_rate;
  const rec = data?.recommendation;
  const totals = data?.totals;
  const fallbackInr = totals
    ? (totals.surepass_rc?.inr || 0) + (totals.surepass_challan?.inr || 0)
    : 0;

  return (
    <div className="bg-white rounded-xl border border-slate-200/80 p-5" data-testid="rep-spend-trend">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <TrendingUp className="w-4 h-4 text-emerald-600" />
        <h3 className="text-sm font-semibold text-slate-700">Vehicle data — what it costs</h3>
        <div className="flex items-center gap-1 ml-2">
          {GRANULARITIES.map(([g, label]) => (
            <button
              key={g}
              onClick={() => setGranularity(g)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                granularity === g
                  ? "bg-slate-900 text-white"
                  : "text-slate-500 hover:bg-slate-100 border border-slate-200"
              }`}
              data-testid={`rep-spend-granularity-${g}`}
            >
              {label}
            </button>
          ))}
        </div>
        {granularity === "custom" && (
          <div className="flex items-center gap-1.5">
            <input
              type="date"
              value={range.start}
              onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
              className="px-2 py-1 text-xs border border-slate-200 rounded-md"
              data-testid="rep-spend-start"
            />
            <span className="text-xs text-slate-400">to</span>
            <input
              type="date"
              value={range.end}
              onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
              className="px-2 py-1 text-xs border border-slate-200 rounded-md"
              data-testid="rep-spend-end"
            />
          </div>
        )}
        <button
          onClick={load}
          disabled={busy}
          className="ml-auto p-1.5 rounded-lg text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
          title="Refresh"
          data-testid="rep-spend-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
        </button>
      </div>

      {!data ? (
        <div className="py-10 text-center text-sm text-slate-400" data-testid="rep-spend-empty">
          {granularity === "custom" ? "Pick a start and end date." : "Loading…"}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
            <Stat label="Total in window" value={`₹${inr(totals?.total_inr)}`} testid="rep-spend-total" />
            <Stat label="Run rate / week" value={`₹${inr(run?.total_inr)}`} testid="rep-spend-weekly" />
            <Stat label="Cashfree RC" value={`₹${inr(totals?.cashfree_rc?.inr)}`} testid="rep-spend-cashfree" />
            <Stat
              label="CarInfo"
              value={`₹${inr((totals?.carinfo_challan?.inr || 0) + (totals?.carinfo_rc?.inr || 0))}`}
              sub={`${totals?.carinfo_challan?.calls || 0} challans · ${totals?.carinfo_rc?.calls || 0} RC rescues`}
              testid="rep-spend-carinfo"
            />
            <Stat
              label="BILAPI challans"
              value={`₹${inr(totals?.bilapi_challan?.inr)}`}
              testid="rep-spend-bilapi"
            />
          </div>

          <div className="space-y-1.5 mb-4" data-testid="rep-spend-buckets">
            {buckets.map((b) => (
              <div key={b.key} className="flex items-center gap-3 text-xs">
                <span className="w-28 flex-shrink-0 text-slate-500">{b.label}</span>
                <div className="flex-1 h-4 bg-slate-100 rounded overflow-hidden flex">
                  {BARS.map(([key, colour, label]) => (
                    <div
                      key={key}
                      className={`${colour} h-full transition-all`}
                      style={{ width: `${((b[key]?.inr || 0) / peak) * 100}%` }}
                      title={`${label} ₹${b[key]?.inr || 0} (${b[key]?.calls || 0} calls)`}
                    />
                  ))}
                  <div
                    className="bg-amber-400 h-full"
                    style={{
                      width: `${(((b.surepass_rc?.inr || 0) + (b.surepass_challan?.inr || 0)) / peak) * 100}%`,
                    }}
                    title={`Surepass fallback ₹${(b.surepass_rc?.inr || 0) + (b.surepass_challan?.inr || 0)}`}
                  />
                </div>
                <span className="w-16 text-right font-semibold text-slate-700">₹{inr(b.total_inr)}</span>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-4 text-[11px] text-slate-500 mb-3 flex-wrap">
            {BARS.map(([key, colour, label]) => (
              <span key={key} className="flex items-center gap-1">
                <i className={`inline-block w-2.5 h-2.5 rounded ${colour}`} /> {label} ₹
                {data.rates?.[key]}/call
              </span>
            ))}
            <span className="flex items-center gap-1" data-testid="rep-spend-fallback">
              <i className="inline-block w-2.5 h-2.5 rounded bg-amber-400" />
              Surepass fallback ₹{inr(fallbackInr)}
              <span className="text-slate-400">
                ({(totals?.surepass_rc?.calls || 0) + (totals?.surepass_challan?.calls || 0)} calls at
                an assumed ₹{data.rates?.surepass_rc})
              </span>
            </span>
          </div>

          {rec && (
            <div
              className="px-3 py-2.5 rounded-lg bg-emerald-50 border border-emerald-200 text-[12px] text-emerald-900"
              data-testid="rep-spend-recommendation"
            >
              <strong>Recharge guide · </strong>
              {["cashfree", "carinfo", "bilapi"].map((p) =>
                rec[p] ? (
                  <span key={p}>
                    {p === "cashfree" ? "Cashfree" : p === "carinfo" ? "CarInfo" : "BILAPI"} burns{" "}
                    <strong>₹{inr(rec[p].weekly_inr)}/week</strong>
                    {rec[p].weeks_left != null && (
                      <>
                        {" "}
                        — ₹{inr(rec[p].balance_inr)} left lasts <strong>{cover(rec[p].weeks_left)}</strong>
                      </>
                    )}
                    {rec[p].topup_inr > 0 && (
                      <>
                        ; top up <strong>₹{fmt(rec[p].topup_inr)}</strong>
                      </>
                    )}
                    .{" "}
                  </span>
                ) : null,
              )}
            </div>
          )}
          <p className="mt-2 text-[11px] text-slate-400">
            Counted from every provider call we logged. CarInfo and BILAPI bill every answered
            call — including "no challans found" — and only hard failures are free; a BILAPI
            no-record verdict confirmed on a retry is two billed calls, which is why CarInfo is
            cheaper on a clean car. Cashfree bills successes only. The Surepass fallback has no
            wallet we can read, so its rate is an assumption, tunable via{" "}
            <code>SUREPASS_RC_PRICE_INR</code>.
          </p>
        </>
      )}
    </div>
  );
}
